/**
 * CombatEntrySection - Entry button for dungeon, encounter, and tower tiles
 *
 * Unified component for all combat biome types:
 * - Dungeons (⚔️): Multi-floor challenges with bosses
 * - Encounters (⚔️): Quick combat zones
 * - Towers (🗼): Progressive floor climbing
 *
 * Shows:
 * - Entry button (disabled during cooldown)
 * - Cooldown countdown when applicable
 */

import React from 'react';

// Format cooldown time as "Xm Xs"
const formatCooldown = (cooldownMs) => {
  if (cooldownMs === null) return null;
  const totalSeconds = Math.ceil(cooldownMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

function CombatEntrySection({
  type, // 'dungeon' | 'encounter' | 'tower'
  biomeData,
  nodeId,
  cooldownMs,
  completedTutorials,
  onTriggerTutorial,
  onSetPendingModal,
  onOpenModal, // Function to open the appropriate modal
}) {
  const isOnCooldown = cooldownMs !== null;

  // Determine button text and icon based on type
  const config = {
    dungeon: {
      icon: '⚔️',
      label: 'View Dungeon',
      btnClass: 'wm-btn-dungeon',
      tutorialKey: 'dungeonEntry',
    },
    encounter: {
      icon: '⚔️',
      label: 'View Encounter',
      btnClass: 'wm-btn-encounter',
      tutorialKey: 'encounterEntry',
    },
    tower: {
      icon: '🗼',
      label: 'View Tower',
      btnClass: 'wm-btn-tower',
      tutorialKey: 'towerEntry',
    },
  }[type];

  const handleClick = () => {
    if (isOnCooldown) return;

    // Check if tutorial needs to show first
    const needsTutorial = !completedTutorials?.includes(config.tutorialKey);
    if (needsTutorial) {
      // Store pending modal and show tutorial first
      onSetPendingModal({ type, biomeId: biomeData.id, nodeId });
      onTriggerTutorial(config.tutorialKey);
    } else {
      // Tutorial already completed, show modal directly
      onOpenModal(biomeData.id, nodeId);
    }
  };

  return (
    <div className="wm-actions">
      <div className="wm-dungeon-entry">
        <button
          className={`wm-btn ${config.btnClass}`}
          disabled={isOnCooldown}
          title={!isOnCooldown ? `View ${type} details` : ''}
          onClick={handleClick}
        >
          <span className="wm-btn-icon">{config.icon}</span>
          <span className="wm-btn-text">
            {isOnCooldown
              ? `Cooldown: ${formatCooldown(cooldownMs)}`
              : config.label}
          </span>
        </button>
      </div>
    </div>
  );
}

export default CombatEntrySection;
